import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  FiBook, 
  FiMail, 
  FiLock, 
  FiUser, 
  FiHome, 
  FiArrowRight, 
  FiArrowLeft,
  FiEye, 
  FiEyeOff, 
  FiCheckCircle, 
  FiShield,
  FiCpu,
  FiAlertCircle
} from 'react-icons/fi';
import ThemeSwitcher from '../components/ThemeSwitcher';
import axios from 'axios';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isLogin, setIsLogin] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') !== 'register';
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [colleges, setColleges] = useState([]);
  const [collegesLoading, setCollegesLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [registerData, setRegisterData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'student',
    collegeId: ''
  });

  const API_URL = `${process.env.REACT_APP_API_URL}/api`;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'login') {
      setIsLogin(true);
    }
  }, [location.search]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  const fetchColleges = useCallback(async () => {
    try {
      setCollegesLoading(true);
      const response = await axios.get(`${API_URL}/colleges`);
      setColleges(response.data || []);

      if (response.data.length > 0 && !registerData.collegeId) {
        setRegisterData(prev => ({
          ...prev,
          collegeId: response.data[0]._id
        }));
      }
    } catch (err) {
      console.error('Error fetching colleges:', err);
      setError('Could not load colleges list. Please refresh or try again.');
    } finally {
      setCollegesLoading(false);
    }
  }, [API_URL, registerData.collegeId]);

  useEffect(() => {
    if (!isLogin && colleges.length === 0) {
      fetchColleges();
    }
  }, [isLogin, colleges.length, fetchColleges]);

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterData({
      ...registerData,
      [name]: value
    });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (!loginData.email || !loginData.password) {
      setError('Email and password are required');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/auth/login`, {
        email: loginData.email.trim(),
        password: loginData.password
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      if (onLogin && typeof onLogin === 'function') {
        onLogin(response.data.token, response.data.user);
      }

      setSuccess('Login successful! Redirecting...');
      navigate('/');
    } catch (err) {
      console.error("Login error:", err.response?.data || err.message);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const { name, email, password, role, collegeId } = registerData;

    if (!name?.trim()) {
      setError('Name is required');
      setLoading(false);
      return;
    }
    if (!email?.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }
    if (!password) {
      setError('Password is required');
      setLoading(false);
      return;
    }
    if (!collegeId) {
      setError('Please select a college institution');
      setLoading(false);
      return;
    }

    const selectedCollege = colleges.find(c => c._id === collegeId);
    if (selectedCollege && selectedCollege.domain) {
      const emailDomain = email.trim().toLowerCase().split('@')[1];
      const reqDomain = selectedCollege.domain.toLowerCase().replace(/^@/, '');
      if (emailDomain !== reqDomain) {
        setError(`Email domain mismatch! Registration for ${selectedCollege.collegeName} requires an email ending with @${reqDomain}`);
        setLoading(false);
        return;
      }
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      const dataToSend = {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password: password,
        role: role,
        collegeId: collegeId
      };

      const response = await axios.post(`${API_URL}/auth/register`, dataToSend);

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      if (onLogin && typeof onLogin === 'function') {
        onLogin(response.data.token, response.data.user);
      }

      setSuccess('Registration successful! Redirecting...');
      navigate('/');
    } catch (err) {
      console.error("Registration error:", err);
      setError(err.response?.data?.message || 'Registration failed. Please check your inputs.');
    } finally {
      setLoading(false);
    }
  };

  const selectedCollegeObj = colleges.find(c => c._id === registerData.collegeId);

  return (
    <div className="auth-page-wrapper">
      <div className="auth-top-bar" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', padding: '20px 48px', boxSizing: 'border-box' }}>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => navigate('/landing')}
        >
          <FiArrowLeft /> Back to Home
        </button>
        <ThemeSwitcher />
      </div>

      <div className="auth-container">
        {/* Left Hero Side Banner */}
        <div className="auth-hero">
          <div className="auth-hero-decor"></div>
          
          <div className="auth-hero-brand">
            <div className="auth-hero-logo">
              <FiBook />
            </div>
            <span className="auth-hero-title">Notebook Portal</span>
          </div>

          <div className="auth-hero-body">
            <h2 className="auth-hero-heading">Empower Your College Learning</h2>
            <p className="auth-hero-subtext">
              Access verified study notes, AI-generated summaries, and past papers in a unified academic workspace.
            </p>

            <div className="auth-hero-features">
              <div className="auth-hero-feature-item">
                <div className="auth-hero-feature-icon"><FiShield /></div>
                <span>Domain-Verified College Institution Security</span>
              </div>
              <div className="auth-hero-feature-item">
                <div className="auth-hero-feature-icon"><FiCpu /></div>
                <span>LLaMA 3.3 AI PDF Note Summaries</span>
              </div>
              <div className="auth-hero-feature-item">
                <div className="auth-hero-feature-icon"><FiCheckCircle /></div>
                <span>Peer-Reviewed Teacher Moderation</span>
              </div>
            </div>
          </div>

          <div className="auth-hero-footer">
            © {new Date().getFullYear()} Notebook Portal • Built for High Academic Performance
          </div>
        </div>

        {/* Right Form Card */}
        <div className="auth-form-card">
          <div className="auth-header">
            <button
              type="button"
              className="btn btn-ghost btn-sm mb-2"
              style={{ padding: 0, height: 'auto', color: 'var(--text-secondary)' }}
              onClick={() => navigate('/landing')}
            >
              <FiArrowLeft /> Back to Home
            </button>
            <h1 className="auth-title">
              {isLogin ? 'Welcome back' : 'Join your college space'}
            </h1>
            <p className="auth-subtitle">
              {isLogin 
                ? 'Sign in to access your notes library and AI workspace.' 
                : 'Create an account using your official institution email.'}
            </p>
          </div>

          {/* Toggle Switch */}
          <div className="auth-tab-switch">
            <button
              type="button"
              className={`auth-tab-btn ${isLogin ? 'active' : ''}`}
              onClick={() => { setIsLogin(true); setError(''); setSuccess(''); }}
            >
              Sign In
            </button>
            <button
              type="button"
              className={`auth-tab-btn ${!isLogin ? 'active' : ''}`}
              onClick={() => { setIsLogin(false); setError(''); setSuccess(''); }}
            >
              Register
            </button>
          </div>

          {error && (
            <div className="alert alert-error">
              <FiAlertCircle style={{ fontSize: '18px', flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <FiCheckCircle style={{ fontSize: '18px', flexShrink: 0 }} />
              <span>{success}</span>
            </div>
          )}

          {isLogin ? (
            /* Sign In Form */
            <form onSubmit={handleLogin} autoComplete="on">
              <div className="form-group">
                <label htmlFor="login-email">Email Address</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon-left" />
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    className="form-input has-left-icon"
                    value={loginData.email}
                    onChange={handleLoginChange}
                    placeholder="student@college.ac.in"
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="login-password">Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon-left" />
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-input has-left-icon has-right-btn"
                    value={loginData.password}
                    onChange={handleLoginChange}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary w-100 mt-4" 
                disabled={loading}
                style={{ height: '46px', fontSize: '15px' }}
              >
                {loading ? 'Signing in...' : 'Sign In to Portal'} <FiArrowRight />
              </button>
            </form>
          ) : (
            /* Register Form */
            <form onSubmit={handleRegister} autoComplete="on">
              <div className="form-group">
                <label htmlFor="register-name">Full Name</label>
                <div className="input-with-icon">
                  <FiUser className="input-icon-left" />
                  <input
                    id="register-name"
                    type="text"
                    name="name"
                    className="form-input has-left-icon"
                    value={registerData.name}
                    onChange={handleRegisterChange}
                    placeholder="Tejas Ambaliya"
                    autoComplete="name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="register-college">College Institution</label>
                <div className="input-with-icon">
                  <FiHome className="input-icon-left" />
                  <select
                    id="register-college"
                    name="collegeId"
                    className="form-input has-left-icon"
                    value={registerData.collegeId}
                    onChange={handleRegisterChange}
                    required
                    disabled={collegesLoading || colleges.length === 0}
                  >
                    <option value="">
                      {collegesLoading ? 'Loading colleges...' : colleges.length === 0 ? 'No colleges available' : 'Select your college'}
                    </option>
                    {colleges.map(c => (
                      <option key={c._id} value={c._id}>
                        {c.collegeName} ({c.collegeCode}) {c.domain ? `- @${c.domain}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                {selectedCollegeObj?.domain && (
                  <div className="domain-hint-box">
                    <FiShield style={{ fontSize: '14px', flexShrink: 0 }} />
                    <span>Must end with <strong>@{selectedCollegeObj.domain}</strong></span>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="register-email">College Email</label>
                <div className="input-with-icon">
                  <FiMail className="input-icon-left" />
                  <input
                    id="register-email"
                    type="email"
                    name="email"
                    className="form-input has-left-icon"
                    value={registerData.email}
                    onChange={handleRegisterChange}
                    placeholder={selectedCollegeObj?.domain ? `user@${selectedCollegeObj.domain}` : "email@domain.ac.in"}
                    autoComplete="username"
                    required
                  />
                </div>
              </div>

              {/* Public registration is strictly for student accounts */}

              <div className="form-group">
                <label htmlFor="register-password">Create Password</label>
                <div className="input-with-icon">
                  <FiLock className="input-icon-left" />
                  <input
                    id="register-password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    className="form-input has-left-icon has-right-btn"
                    value={registerData.password}
                    onChange={handleRegisterChange}
                    placeholder="Min. 6 characters"
                    autoComplete="new-password"
                    required
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword(!showPassword)}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className="btn btn-primary w-100 mt-4" 
                disabled={loading || collegesLoading || colleges.length === 0}
                style={{ height: '46px', fontSize: '15px' }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;